import { CallbackRegistry, UnsubscribeFunction, default as CallbackFactory } from "callback-registry";
import { Glue42Core } from "@glue42/core";
import { PendingWindowState, WindowEvents, WindowWrapper } from "./types";

export class WindowStore {
    private readonly waitForTimeoutInMilliseconds: number = 60000;
    private _windows: { [key: string]: WindowWrapper } = {};
    private _pendingWindows: { [key: string]: WindowWrapper } = {};
    private _pendingWindowsStates: { [key: string]: PendingWindowState } = {};
    private _registry: CallbackRegistry;
    private _logger: Glue42Core.Logger.API;

    constructor() {
        this._registry = CallbackFactory();
    }

    public init(logger: Glue42Core.Logger.API) {
        this._logger = logger;
    }

    // Returns a window which either in Ready OR NOT 'ready' state (when the window is NOT marked with readyToShow)
    public get(id: string): WindowWrapper {
        return this._windows[id] || this._pendingWindows[id];
    }

    // Returns a window which is in 'ready' state (when the window is marked with readyToShow)
    public getIfReady(id: string): WindowWrapper {
        return this._windows[id];
    }

    public get list(): { [key: string]: WindowWrapper } {
        return this._windows;
    }

    public add(window: WindowWrapper) {
        const isExist = this._pendingWindows[window.API.id] ? true : false;
        if (isExist) {
            this._logger.error(`trying to add window with id ${window.API.id} from windowStore, which already exists`);
            return;
        }
        const remote = window.API.windowType === "remote";
        this._pendingWindows[window.API.id] = window;
        this._pendingWindowsStates[window.API.id] = {
            ready: false,
            urlChanged: remote, // remote windows don't have url events
        };

        this._registry.execute("on-added", window);
    }

    public remove(window: WindowWrapper) {
        delete this._windows[window.API.id];
        delete this._pendingWindows[window.API.id];
        delete this._pendingWindowsStates[window.API.id];
        this._registry.execute("on-removed", window);
    }

    public setReadyState(windowId: string) {
        const targetWindowState = this._pendingWindowsStates[windowId];

        if (typeof targetWindowState === "undefined") {
            return;
        }

        targetWindowState.ready = true;

        if (targetWindowState.urlChanged) {
            this.markReadyToShow(windowId);
        }
    }

    public setUrlChangedState(windowId: string) {
        const targetWindowState = this._pendingWindowsStates[windowId];

        if (typeof targetWindowState === "undefined") {
            return;
        }

        targetWindowState.urlChanged = true;

        if (targetWindowState.ready) {
            this.markReadyToShow(windowId);
        }
    }

    public setCompositionChangedState(windowId: string, windowAvailableOnURLChanged: boolean) {
        // returning window obj earlier than urlChanged, if windowAvailableOnURLChanged is missing a value (older versions of GlueDesktop), the window obj is returned on URL changed
        const targetWindowState = this._pendingWindowsStates[windowId];

        if (typeof targetWindowState === "undefined") {
            return;
        }

        if (windowAvailableOnURLChanged === false) {
            this.markReadyToShow(windowId);
        }
    }

    public waitFor(id: string): Promise<WindowWrapper> {
        return new Promise((resolve, reject) => {

            let un: UnsubscribeFunction;

            const timeout = setTimeout(() => {
                un();
                reject("waitFor timed out.");
            }, this.waitForTimeoutInMilliseconds);

            const win = this._windows[id];
            if (win) {
                clearTimeout(timeout);
                resolve(win);
            } else {
                un = this.onReadyWindow((w: WindowWrapper) => {
                    if (w.API.id !== id) {
                        return;
                    }
                    clearTimeout(timeout);
                    un();
                    resolve(w);
                });
            }
        });
    }

    public onReadyWindow(callback: (win: WindowWrapper) => void): UnsubscribeFunction {
        return this._registry.add("on-ready", callback);
    }

    public onAdded(callback: (win: WindowWrapper) => void): UnsubscribeFunction {
        return this._registry.add("on-added", callback);
    }

    public onRemoved(callback: (win: WindowWrapper) => void): UnsubscribeFunction {
        return this._registry.add("on-removed", callback);
    }

    // newly created windows now need to hear both "ready" and "urlChanged" events in otder to be markedReadyToShow
    public markReadyToShow(windowId: string) {
        if (this._pendingWindows[windowId]) {
            this._windows[windowId] = this._pendingWindows[windowId];
            delete this._pendingWindows[windowId];
            delete this._pendingWindowsStates[windowId];
        }

        this._registry.execute("on-ready", this._windows[windowId]);
    }
}

export default new WindowStore();
